import axios from 'axios';
import { store } from '@/store';
import { sendAuthRedirect } from '@/router/utils/router.util';
import { auth, getAccessToken } from '@/plugins/auth';

// Module-scoped guard that prevents a 401 storm (typical during boot, when
// many parallel API calls fire at once and a stale-token / wrong-audience
// rejects them all) from triggering N concurrent loginWithRedirect calls —
// which would full-page redirect to Auth0, return with a fresh code, and 401
// again, creating an infinite reload loop. The flag resets naturally on the
// next page load (module re-import).
let auth0RedirectInProgress = false;

const triggerAuth0LoginRedirect = () => {
    if (auth0RedirectInProgress || !auth) {
        return;
    }
    auth0RedirectInProgress = true;

    const currentRoute = window.location.pathname + window.location.search;

    sessionStorage.setItem('auth_redirect_target', currentRoute);

    auth.loginWithRedirect({
        appState: { target: currentRoute },
    });
};

export default {
    setup(router) {
        axios.interceptors.request.use(async (config) => {
            // For non-embedded (standalone) usage, source the bearer token
            // from the Auth0 SDK (which handles silent refresh) instead of
            // the static Vuex session.jwt. Embedded mode keeps the JWT-in-URL
            // contract via state.auth.session.jwt.
            if (auth?.isAuthenticated?.value) {
                try {
                    const accessToken = await getAccessToken();

                    if (accessToken) {
                        config.headers.Authorization = `Bearer ${accessToken}`;
                    }
                } catch (e) {
                    // eslint-disable-next-line no-console
                    if (import.meta.env.DEV) console.warn('Auth0 token fetch failed, request will fly without Authorization', e);
                }
            } else {
                const { jwt } = store.state.auth.session;

                if (jwt) {
                    config.headers.Authorization = `Bearer ${jwt}`;
                }
            }

            if (!config.data) {
                config.data = {};
            }

            return config;
        });

        axios.interceptors.response.use((response) => {
            return response;
        }, async (error) => {
            const { response, config } = error;

            if (!response) {
                return Promise.reject(error);
            }

            // Auth0-specific 401 retry-once then redirect.
            if (
                response.status === 401
                && auth?.isAuthenticated?.value
                && !config?.__authRetry
                && !auth0RedirectInProgress
            ) {
                // Force a non-cached token fetch and retry the original
                // request once. Covers the case where the SDK's cached access
                // token is stale (clock skew, race with backend rotation) but
                // the refresh token is still valid — avoids a full Universal
                // Login round-trip for what is really just a renewable token.
                try {
                    const token = await getAccessToken({ ignoreCache: true });

                    if (token) {
                        config.__authRetry = true;
                        config.headers = config.headers || {};
                        config.headers.Authorization = `Bearer ${token}`;

                        return await axios.request(config);
                    }
                } catch (renewError) {
                    // eslint-disable-next-line no-console
                    if (import.meta.env.DEV) console.warn('Token renewal failed after 401, redirecting to login', renewError);
                }

                triggerAuth0LoginRedirect();

                return Promise.reject(error);
            }

            if (response.status === 403 && store?.state?.auth?.user) {
                return sendAuthRedirect(router, '/access-denied');
            }

            return Promise.reject(error);
        });
    },
};
